import { useEffect, useState } from "react";
import {
  Select,
  MenuItem,
  CircularProgress,
  FormControl,
  InputLabel,
  FormHelperText,
  SelectChangeEvent,
  SelectProps,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchGeneriqueDataByType } from "../../reduxStore/GeneriqueDataSlice";
import { RootState, AppDispatch } from "../../reduxStore/Store";
import { GeneriqueData } from "../../models/models";
import { GeneriqueDataType } from "../../constants/enums";

interface SelectNatureReglementProps
  extends Omit<SelectProps<number | string>, "value" | "onChange"> {
  onNatureReglementChange: (generiqueData: GeneriqueData | null) => void;
  selectedNatureReglement?: GeneriqueData | null;
  /** Affiche une option « Toutes les natures » (valeur vide) — utile en filtre */
  includeAllOption?: boolean;
}



const SelectNatureReglement: React.FC<SelectNatureReglementProps> = ({
  onNatureReglementChange,
  selectedNatureReglement,
  includeAllOption = false,
  ...selectProps
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { natureReglementData, loading, error } = useSelector(
    (state: RootState) => state.GeneriqueDataSlice
  );
  const [natureReglementId, setNatureReglementId] = useState<number | string>("");

  useEffect(() => {
    dispatch(fetchGeneriqueDataByType({ type:GeneriqueDataType.NATURE_REGLEMENT}));
  }, [dispatch]);

  useEffect(() => {
    if (
      selectedNatureReglement &&
      natureReglementData.some((item) => item.id === selectedNatureReglement.id)
    ) {
      setNatureReglementId(selectedNatureReglement.id);
    } else if (!selectedNatureReglement) {
      // Sélection annulée → revient sur « Toutes »
      setNatureReglementId("");
    }
  }, [selectedNatureReglement, natureReglementData]);

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    const selectedId = event.target.value;
    const selectedNatureReglement =
      natureReglementData.find((item) => item.id === Number(selectedId)) || null;
    setNatureReglementId(selectedId);
    onNatureReglementChange(selectedNatureReglement);
  };

  // 🌀 Avoid rendering Select while loading and data is still empty
  if (loading && natureReglementData.length === 0) {
    return <CircularProgress />;
  }

  return (
    <FormControl fullWidth size="small" variant="outlined" error={!!error}>
      <InputLabel id="select-nature-reglement-label">Nature de Règlement</InputLabel>
      <Select
        labelId="select-nature-reglement-label"
        value={natureReglementId}
        onChange={handleChange}
        label="Nature de Règlement"
        size="small"
        {...selectProps}
      >
        {natureReglementData.length === 0 ? (
          <MenuItem value="">Aucune nature de règlement trouvée</MenuItem>
        ) : (
          [
            includeAllOption && (
              <MenuItem key="__all__" value="">
                <em>Toutes les natures</em>
              </MenuItem>
            ),
            ...natureReglementData.map((natureItem) => (
              <MenuItem key={natureItem.id} value={natureItem.id}>
                {natureItem.nom}
              </MenuItem>
            )),
          ]
        )}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default SelectNatureReglement;
